"use client";
import { type UserInfoData, getUserInfo } from "@/lib/userInfo";
import type { Countries } from "@/queries/getCountries";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import type { Departure, Travel } from "services/travel";
import { requestProgramOpenStore } from "../RequestProgram";
import { BaseForm } from "../RequestProgram/BaseForm";
import { getLastRequestProgram, setLastRequestProgram } from "../RequestProgram/lastRequestProgram";
import {
  type RequestProgramPosthogOpenEventSource,
  requestProgramEvent,
} from "../RequestProgram/requestProgramPosthog";
import { requestProgrammeOnSuccessServer } from "../RequestProgram/requestProgrammeOnSuccessServer";

type Props = {
  travel: Travel;
  departure: Departure;
  countries: Countries;
};
export const RequestProgramPopupInner = ({ travel, countries, departure }: Props) => {
  const [triggered, setTriggered] = useState(false);
  const [show, setShow] = useState<null | {
    source: RequestProgramPosthogOpenEventSource;
    userInfo: UserInfoData | null;
  }>(null);

  const handleClick = async (source: RequestProgramPosthogOpenEventSource) => {
    const userInfo = await getUserInfo();

    if (userInfo) {
      setShow({ source, userInfo });
    } else {
      setShow({ source, userInfo: null });
    }
  };

  const handleTrigger = (source: RequestProgramPosthogOpenEventSource) => {
    if (!triggered && requestProgramOpenStore.get() === null && window.innerWidth > 768) {
      setTriggered(true);

      const lastTime = getLastRequestProgram();
      if (lastTime === null || dayjs().diff(lastTime, "day") > 30) {
        setLastRequestProgram();
        requestProgramEvent({
          step: "open",
          source,
          travelId: travel.id,
          versionId: departure.versionId,
          departureId: departure.id,
        });
        handleClick(source);
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const timeout = setTimeout(() => {
      handleTrigger("auto_timer");
    }, 25 * 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [triggered]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > document.body.scrollHeight / 2) {
        handleTrigger("auto_scroll");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [triggered]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        handleTrigger("auto_pageleave"); // TODO use more robust method
      }
    };

    window.addEventListener("mousemove", handleMove);
    return () => {
      window.removeEventListener("mousemove", handleMove);
    };
  }, [triggered]);

  const handleSuccess = () => {
    requestProgrammeOnSuccessServer(travel, departure);
  };

  return (
    <>
      {show !== null && (
        <BaseForm
          departureId={departure.id}
          onSuccess={handleSuccess}
          onHide={() => {
            requestProgramEvent({
              step: "close",
              source: show.source,
              travelId: travel.id,
              versionId: departure.versionId,
              departureId: departure.id,
            });
            setShow(null);
          }}
          countries={countries}
          travelId={travel.id}
          versionId={departure.versionId}
          travelTitle={travel.title}
          travelImage={travel.travelImages[0] || null}
          departure={departure}
          travelCountries={travel.countries}
          small
          programUrl={departure.infosupUrl}
          sendEmail={false}
          initData={show.userInfo === null ? undefined : show.userInfo}
          source={show.source}
        />
      )}
    </>
  );
};
